import * as React from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface TabItem {
  value: string;
  label: string;
  icon?: LucideIcon;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  responsive?: boolean;
  variant?: 'default' | 'underline';
  size?: 'sm' | 'default';
  className?: string;
}

/* ─── useMediaQuery ────────────────────────────────────────────────────────── */

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/* ─── Size class map ──────────────────────────────────────────────────────── */

const sizeClasses = {
  sm: 'text-xs py-1 px-2',
  default: 'px-3 py-2 text-sm',
} as const;

/* ─── Tabs ─────────────────────────────────────────────────────────────────── */

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ items, value, onChange, responsive = true, variant = 'underline', size = 'default', className }, ref) => {
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const showDropdown = responsive && !isDesktop;

    const selectedItem = items.find((item) => item.value === value);

    if (showDropdown) {
      return (
        <div ref={ref} className={cn('relative', className)}>
          <div className="relative">
            <select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(
                'w-full appearance-none bg-raios-card border border-raios-border rounded-md',
                'text-sm text-raios-text pl-3 pr-10 py-2.5 min-h-[44px]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:border-raios-accent',
                'transition-colors cursor-pointer'
              )}
              aria-label="Select tab"
            >
              {items.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              {selectedItem?.icon ? (
                <selectedItem.icon className="h-4 w-4 text-raios-muted mr-1" aria-hidden="true" />
              ) : null}
              <ChevronDown className="h-4 w-4 text-raios-muted" aria-hidden="true" />
            </div>
          </div>
        </div>
      );
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = items.findIndex((item) => item.value === value);
      let nextIndex: number | null = null;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % items.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + items.length) % items.length;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = items.length - 1;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        onChange(items[nextIndex].value);
        const tabEl = (e.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex];
        tabEl?.focus();
      }
    };

    const isUnderline = variant === 'underline';

    return (
      <div
        ref={ref}
        role="tablist"
        onKeyDown={handleKeyDown}
        className={cn(
          'flex gap-1',
          isUnderline && 'border-b border-raios-border',
          className
        )}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.value === value;

          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(item.value)}
              className={cn(
                'inline-flex items-center gap-2 font-medium transition-colors cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40',
                sizeClasses[size],
                isUnderline
                  ? cn(
                      '-mb-px rounded-t-md',
                      isActive
                        ? 'border-b-2 border-raios-accent text-raios-accent-text'
                        : 'text-raios-muted hover:text-raios-text-secondary border-b-2 border-transparent'
                    )
                  : cn(
                      'rounded-full',
                      isActive
                        ? 'bg-raios-accent/15 text-raios-accent-text'
                        : 'text-raios-muted hover:text-raios-text-secondary hover:bg-raios-elevated/50'
                    )
              )}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';

// eslint-disable-next-line react-refresh/only-export-components
export { Tabs, useMediaQuery };
export type { TabsProps, TabItem };
